import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { Component, inject } from '@angular/core';
import { AdminButtonComponent } from './admin-button.component';
import { AdminEmptyStateComponent } from './admin-empty-state.component';
import { AdminSidePanelComponent } from './admin-side-panel.component';
import { NotificationsService } from '../services/notifications.service';
import type { NotificationDto } from '../models/notifications.models';
import { FeatureRequestStatusBadgeComponent } from '../../platform/pages/feature-requests/components/feature-request-status-badge.component';
import type { FeatureRequestStatus } from '../models/feature-request.models';

@Component({
  selector: 'app-admin-notification-panel',
  standalone: true,
  imports: [DatePipe, NgTemplateOutlet, AdminButtonComponent, AdminEmptyStateComponent, AdminSidePanelComponent, FeatureRequestStatusBadgeComponent],
  template: `
    @if (notifications.panelOpen()) {
      @if (useSidePanel()) {
        <app-admin-side-panel title="Notifications" (closed)="notifications.closePanel()">
          <ng-container [ngTemplateOutlet]="panelBody" />
        </app-admin-side-panel>
      } @else {
        <div class="cg-admin-notif-panel" role="dialog" aria-label="Notifications">
          <ng-container [ngTemplateOutlet]="panelBody" />
        </div>
      }
    }

    <ng-template #panelBody>
      <div class="cg-admin-notif-panel__inner">
        @if (!useSidePanel()) {
          <header class="cg-admin-notif-panel__header">
            <h2 class="cg-admin-notif-panel__title">Notifications</h2>
            <div class="cg-admin-notif-panel__actions">
              <app-admin-button
                variant="ghost"
                type="button"
                [disabled]="notifications.notifications().length === 0"
                (click)="onMarkAllRead()"
              >
                Mark all read
              </app-admin-button>
              <app-admin-button
                variant="ghost"
                type="button"
                [disabled]="notifications.notifications().length === 0"
                (click)="onClearAll()"
              >
                Clear all
              </app-admin-button>
            </div>
          </header>
        } @else {
          <div class="cg-admin-notif-panel__actions cg-admin-notif-panel__actions--mobile">
            <app-admin-button
              variant="ghost"
              type="button"
              [disabled]="notifications.notifications().length === 0"
              (click)="onMarkAllRead()"
            >
              Mark all read
            </app-admin-button>
            <app-admin-button
              variant="ghost"
              type="button"
              [disabled]="notifications.notifications().length === 0"
              (click)="onClearAll()"
            >
              Clear all
            </app-admin-button>
          </div>
        }

        @if (notifications.loading()) {
          <p class="cg-admin-notif-panel__status">Loading notifications…</p>
        } @else if (notifications.notifications().length === 0) {
          <app-admin-empty-state title="No notifications" description="You're all caught up." />
        } @else {
          <ul class="cg-admin-notif-panel__list">
            @for (item of notifications.notifications(); track item._id) {
              <li>
                <button
                  type="button"
                  class="cg-admin-notif-item"
                  [class.cg-admin-notif-item--unread]="!item.readAt"
                  (click)="onItemClick(item)"
                >
                  <span class="cg-admin-notif-item__title">{{ item.title }}</span>
                  @if (featureRequestStatus(item); as status) {
                    <app-feature-request-status-badge [status]="status" size="sm" />
                  }
                  <span class="cg-admin-notif-item__message">{{ item.message }}</span>
                  <span class="cg-admin-notif-item__time">{{ item.createdAt | date: 'medium' }}</span>
                </button>
              </li>
            }
          </ul>
        }
      </div>
    </ng-template>
  `,
})
export class AdminNotificationPanelComponent {
  readonly notifications = inject(NotificationsService);

  useSidePanel(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;
  }

  onMarkAllRead(): void {
    void this.notifications.markAllRead();
  }

  onClearAll(): void {
    void this.notifications.clearAll();
  }

  onItemClick(item: NotificationDto): void {
    void this.notifications.navigateTo(item);
  }

  featureRequestStatus(item: NotificationDto): FeatureRequestStatus | null {
    if (item.entityType !== 'feature_request') return null;
    const status = item.metadata?.['newStatus'];
    if (typeof status !== 'string') return null;
    const allowed: FeatureRequestStatus[] = ['NEW', 'ACCEPTED', 'IN_PROGRESS', 'DONE', 'CLOSED'];
    return allowed.includes(status as FeatureRequestStatus) ? (status as FeatureRequestStatus) : null;
  }
}
